/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useTranslation } from 'react-i18next'
import { isEmpty } from 'lodash'
import { useSetRecoilState } from 'recoil'
import { openModalState } from 'recoils/atoms'

import { Button, Modal } from 'components'
import {
  initialFormTransferringTicket,
  schemaFormTransferringTicket,
  IResponseTicket,
} from 'features/Ticket'

import TicketTransferDetail from './TicketTransferDetail'
import TicketTransferForm from './TicketTransferForm'

import { setFormatAssignDate } from 'helpers/common'
import { usePromptForm } from 'helpers/hooks'

import type {
  ISchemaFormTransferringTicket,
  MapIUsersInfo,
  IUserSchema,
  ITransferReasonsSchema,
} from '../model'

interface ITransferModalProps {
  id: string
  isOpen: boolean
  ticket: IResponseTicket
  onClose: (e: any) => void
  onRefreshDetail: () => void
  payloadBeforeSubmit?: ISchemaFormTransferringTicket | null
  setPayloadBeforeSubmit: (
    payload?: ISchemaFormTransferringTicket | null,
  ) => void
  setOpenTransferTicket: () => void
  setConfirmModal: () => void
}

interface SetValueType {
  key: string
  value?: string | number | Date | IUserSchema[] | ITransferReasonsSchema[]
}

function TicketTransferModal(props: ITransferModalProps): JSX.Element {
  const {
    isOpen,
    ticket,
    onClose,
    payloadBeforeSubmit,
    setPayloadBeforeSubmit,
    setOpenTransferTicket,
    setConfirmModal,
  } = props

  const {
    code,
    type,
    statusPriority,
    requestDate,
    users,
    assignStart,
    assignEnd,
    issueDetail,
  } = ticket

  const { t } = useTranslation(['ticket', 'common'])
  const setIsOpenModal = useSetRecoilState(openModalState)

  const methods = useForm({
    defaultValues: initialFormTransferringTicket,
    resolver: yupResolver(schemaFormTransferringTicket),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  })

  const base = 'f-ticket-transfer-modal'

  const [shouldPrompt, setShouldPrompt] = usePromptForm(methods)

  const setFormatDate = (date: string) => setFormatAssignDate(new Date(date))

  const assignDate = setFormatDate(requestDate)

  const beforeSubmit = async (value: ISchemaFormTransferringTicket) => {
    if (!isEmpty(value.users)) {
      setConfirmModal()
      setPayloadBeforeSubmit(value)

      await setTimeout(() => {
        setShouldPrompt(false)
      }, 2000)
    }
  }

  const formatUsers = () => {
    const usersTicket = users.map((value) => {
      return { value: value.id, key: value.id, label: value.fullName }
    })

    return usersTicket
  }

  useEffect(() => {
    if (isEmpty(payloadBeforeSubmit)) {
      methods.reset({
        ...initialFormTransferringTicket,
        assignStart: assignStart
          ? new Date(assignStart)
          : new Date(new Date().setHours(8, 0, 0)),
        assignEnd: assignEnd
          ? new Date(assignEnd)
          : new Date(new Date().setHours(9, 0, 0)),
        assignDate: assignStart ? new Date(assignStart) : new Date(),
        users: formatUsers() as Array<MapIUsersInfo>,
        statusPriority,
        issueDetail,
      })
    }
  }, [users])

  useEffect(() => {
    if (!isEmpty(payloadBeforeSubmit)) setupDefaultValueBeforeSubmit()
  }, [])

  const _setValue = ({ key, value }: SetValueType) => {
    methods.setValue(key, value, {
      shouldDirty: true,
    })
  }

  const setupDefaultValueBeforeSubmit = () => {
    _setValue({ key: 'assignStart', value: payloadBeforeSubmit?.assignStart })
    _setValue({ key: 'assignEnd', value: payloadBeforeSubmit?.assignEnd })
    _setValue({ key: 'assignDate', value: payloadBeforeSubmit?.assignDate })
    _setValue({ key: 'issueDetail', value: payloadBeforeSubmit?.issueDetail })
    _setValue({ key: 'users', value: payloadBeforeSubmit?.users })
    _setValue({
      key: 'statusPriority',
      value: payloadBeforeSubmit?.statusPriority,
    })
    _setValue({
      key: 'transferringDetail',
      value: payloadBeforeSubmit?.transferringDetail,
    })
    _setValue({
      key: 'transferringReasons',
      value: payloadBeforeSubmit?.transferringReasons,
    })
  }

  const handleCloseModal = () => {
    setIsOpenModal(false)

    if (shouldPrompt) {
      onClose(methods.watch())
    } else {
      setOpenTransferTicket()
      setPayloadBeforeSubmit(null)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleCloseModal}>
      <div id='assign-content-form' className={`${base}__content`}>
        <div className={`${base}__modal-header`}>
          <span className={`${base}__modal-header--title`}>
            {t('ticket:ticketId.title')} {code || '-'}
          </span>
        </div>

        <TicketTransferDetail type={type.nameTh} assignDate={assignDate} />

        <FormProvider {...methods}>
          <form
            className={`${base}__form`}
            onSubmit={methods.handleSubmit(beforeSubmit)}
          >
            <TicketTransferForm users={users} statusPriority={statusPriority} />
            <div className={`${base}__container-button`}>
              <Button
                className={`${base}__button--action-button`}
                variants='secondary'
                onClick={handleCloseModal}
              >
                {t('common:cancel.button')}
              </Button>
              <Button
                className={`${base}__button--action-button`}
                type='submit'
              >
                {t('ticket:appointment.button')}
              </Button>
            </div>
          </form>
        </FormProvider>
      </div>
    </Modal>
  )
}

export default TicketTransferModal
